/**
 * @fileoverview Future Protea Cricket Admin Application
 * @module App
 *
 * @description
 * Root component with routing, providers, and layout.
 * Cricket management admin panel.
 */

import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ThemeProvider } from '@/contexts/ThemeProvider';
import { AuthProvider } from '@/contexts/AuthContext';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/app-sidebar';
import { SiteHeader } from '@/components/site-header';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { PermissionGuard } from '@/components/PermissionGuard';
import type { PermissionName } from '@/config/permissions';
import { Toaster } from '@/components/ui/sonner';
import { NotFoundPage } from '@/components/NotFoundPage';

// ─────────────────────────────────────────────────────────────
// Auth
// ─────────────────────────────────────────────────────────────
// LoginPage is eager — it's the unauthenticated landing screen and lazy-
// loading it would add a fallback flash on the very first paint.
import { LoginPage } from '@/Auth';
import { LandingPage } from '@/Landing';
import { PrivacyPolicyPage } from '@/PrivacyPolicy';
import { DeleteAccountPage } from '@/DeleteAccount';

// ─────────────────────────────────────────────────────────────
// Cricket Admin Screens (route-level code split)
// ─────────────────────────────────────────────────────────────
// All authenticated pages are lazy-loaded so a spectator landing on the
// login screen doesn't download the entire admin bundle up front. Each
// `lazy(() => import(...).then(m => ({ default: m.X })))` adapter is the
// standard way to lazy-load named exports (React.lazy needs a `default`).
// Login-free share-link destination. Lazy so the scorecard widgets it pulls
// in stay out of the main entry chunk — admins never download it, and a
// spectator arriving from a shared link never downloads the admin routes.
const PublicMatchPage = lazy(() => import('@/PublicMatch/PublicMatchPage').then(m => ({ default: m.PublicMatchPage })));

const DashboardPage = lazy(() => import('@/Admin/Dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
const MatchesPage = lazy(() => import('@/Admin/Matches').then(m => ({ default: m.MatchesPage })));
const CreateMatchPage = lazy(() => import('@/Admin/Matches/CreateMatchPage').then(m => ({ default: m.CreateMatchPage })));
const MatchDetailsPage = lazy(() => import('@/Admin/Matches/MatchDetailsPage').then(m => ({ default: m.MatchDetailsPage })));
const EditMatchPage = lazy(() => import('@/Admin/Matches/EditMatchPage').then(m => ({ default: m.EditMatchPage })));
// Live scoring is owned by the mobile feeder app (protea-app) and the
// admin web no longer hosts it — the page file was removed. StartMatchPage
// is similarly entry-point-less: the toss-and-start handoff lives in the
// mobile app, so this page file is retained but not routed.
const TossPage = lazy(() => import('@/Admin/Matches/TossPage').then(m => ({ default: m.TossPage })));
const SelectPlayingXIPage = lazy(() => import('@/Admin/Matches/SelectPlayingXIPage').then(m => ({ default: m.SelectPlayingXIPage })));
const TournamentsPage = lazy(() => import('@/Admin/Tournaments').then(m => ({ default: m.TournamentsPage })));
const CreateTournamentPage = lazy(() => import('@/Admin/Tournaments/CreateTournamentPage').then(m => ({ default: m.CreateTournamentPage })));
const EditTournamentPage = lazy(() => import('@/Admin/Tournaments/EditTournamentPage').then(m => ({ default: m.EditTournamentPage })));
const TournamentDetailsPage = lazy(() => import('@/Admin/Tournaments/TournamentDetailsPage').then(m => ({ default: m.TournamentDetailsPage })));
const TournamentBracketPage = lazy(() => import('@/Admin/Tournaments/TournamentBracketPage').then(m => ({ default: m.TournamentBracketPage })));
const TeamsPage = lazy(() => import('@/Admin/Teams').then(m => ({ default: m.TeamsPage })));
const CreateTeamPage = lazy(() => import('@/Admin/Teams/CreateTeamPage').then(m => ({ default: m.CreateTeamPage })));
const EditTeamPage = lazy(() => import('@/Admin/Teams/EditTeamPage').then(m => ({ default: m.EditTeamPage })));
const TeamDetailsPage = lazy(() => import('@/Admin/Teams/TeamDetailsPage').then(m => ({ default: m.TeamDetailsPage })));
const PlayersPage = lazy(() => import('@/Admin/Players').then(m => ({ default: m.PlayersPage })));
const RegisterPlayerPage = lazy(() => import('@/Admin/Players/RegisterPlayerPage').then(m => ({ default: m.RegisterPlayerPage })));
const EditPlayerPage = lazy(() => import('@/Admin/Players/EditPlayerPage').then(m => ({ default: m.EditPlayerPage })));
const PlayerJourneyPage = lazy(() => import('@/Admin/Players/PlayerJourneyPage').then(m => ({ default: m.PlayerJourneyPage })));
const ReportsPage = lazy(() => import('@/Admin/Reports').then(m => ({ default: m.ReportsPage })));
const AnalyticsPage = lazy(() => import('@/Admin/Analytics').then(m => ({ default: m.AnalyticsPage })));
const AppHealthPage = lazy(() => import('@/Admin/AppHealth').then(m => ({ default: m.AppHealthPage })));
const UserManagementPage = lazy(() => import('@/Admin/UserManagement').then(m => ({ default: m.UserManagementPage })));
const RolesPermissionsPage = lazy(() => import('@/Admin/UserManagement/roles-page').then(m => ({ default: m.RolesPermissionsPage })));
const SystemSettingsPage = lazy(() => import('@/Admin/SystemSettings').then(m => ({ default: m.SystemSettingsPage })));
const SupportPage = lazy(() => import('@/Admin/Support').then(m => ({ default: m.SupportPage })));
const AnnouncementsPage = lazy(() => import('@/Admin/Announcements').then(m => ({ default: m.AnnouncementsPage })));
const NotificationsPage = lazy(() => import('@/Admin/Notifications').then(m => ({ default: m.NotificationsPage })));
const AdminProfilePage = lazy(() => import('@/Admin/AdminProfile').then(m => ({ default: m.AdminProfilePage })));

// ─────────────────────────────────────────────────────────────
// Layout
// ─────────────────────────────────────────────────────────────

function AdminLayout({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <SidebarProvider
      style={{
        '--sidebar-width': '16rem',
        '--header-height': '3.5rem',
      } as React.CSSProperties}
    >
      <AppSidebar variant="inset" />
      <SidebarInset>
        <SiteHeader title={title} />
        <main className="flex-1 overflow-auto p-4 lg:p-6">{children}</main>
      </SidebarInset>
    </SidebarProvider>
  );
}

/**
 * Route wrapper: enforces auth + admin-role gate (via ProtectedRoute) and
 * optionally a permission gate via PermissionGuard.
 *
 * Today admin/super_admin hold the wildcard '*' so PermissionGuard is a
 * no-op for them. The permission prop documents the required permission
 * per page (security-as-code) and enforces it the moment admin web is
 * opened to non-admin roles with scoped permissions.
 */
function ProtectedPage({
  children,
  title,
  permission,
  anyPermission,
}: {
  children: React.ReactNode;
  title: string;
  permission?: PermissionName;
  anyPermission?: PermissionName[];
}) {
  const body =
    permission || anyPermission ? (
      <PermissionGuard permission={permission} anyPermission={anyPermission}>
        {children}
      </PermissionGuard>
    ) : (
      children
    );
  return (
    <ProtectedRoute>
      <AdminLayout title={title}>{body}</AdminLayout>
    </ProtectedRoute>
  );
}


// ─────────────────────────────────────────────────────────────
// App
// ─────────────────────────────────────────────────────────────

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
          <Suspense fallback={
            <div className="flex h-screen items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          }>
          <Routes>
            {/* Marketing landing page — replaces the old behaviour where
                `/` opened straight onto the admin login form. Anonymous
                visitors land here; signed-in admins are redirected to the
                dashboard from inside [LandingPage]. */}
            <Route path="/" element={<LandingPage />} />

            {/* Auth */}
            <Route path="/login" element={<LoginPage />} />

            {/* Public legal pages — required for Google Play Store listing.
                The deletion-request URL must be reachable without a login so
                users (and Play Store reviewers) can find it after uninstall. */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/delete-my-account" element={<DeleteAccountPage />} />

            {/* Public share link for a single match. Singular `/match/` — the
                admin surface uses plural `/matches/...`, so there is no
                collision. Intentionally outside [ProtectedPage]: a spectator
                following a WhatsApp link has no account. The backend's
                server-rendered page at /api/public/matches/:id/view stays
                live so links already shared keep resolving. */}
            <Route path="/match/:id" element={<PublicMatchPage />} />

            {/* Cricket Admin Screens */}
            <Route path="/dashboard" element={<ProtectedPage title="Dashboard"><DashboardPage /></ProtectedPage>} />

            {/* Matches */}
            <Route path="/matches" element={<ProtectedPage title="Matches" permission="matches.view"><MatchesPage /></ProtectedPage>} />
            <Route path="/matches/create" element={<ProtectedPage title="Create Match" permission="matches.create"><CreateMatchPage /></ProtectedPage>} />
            <Route path="/matches/:id" element={<ProtectedPage title="Match Details" permission="matches.view"><MatchDetailsPage /></ProtectedPage>} />
            <Route path="/matches/:id/edit" element={<ProtectedPage title="Edit Match" permission="matches.update"><EditMatchPage /></ProtectedPage>} />
            <Route path="/matches/:id/toss" element={<ProtectedPage title="Toss" permission="matches.update"><TossPage /></ProtectedPage>} />
            <Route path="/matches/:id/playing-xi" element={<ProtectedPage title="Playing XI" permission="matches.update"><SelectPlayingXIPage /></ProtectedPage>} />
            {/* /matches/:id/start and /matches/:id/score routes removed —
                live scoring and the start-match handoff are owned by the
                mobile feeder app. Direct URL access now falls through to
                the not-found route, so admins won't end up on a stale UI. */}

            {/* Tournaments */}
            <Route path="/tournaments" element={<ProtectedPage title="Tournaments" permission="tournaments.view"><TournamentsPage /></ProtectedPage>} />
            <Route path="/tournaments/create" element={<ProtectedPage title="Create Tournament" permission="tournaments.create"><CreateTournamentPage /></ProtectedPage>} />
            <Route path="/tournaments/:id" element={<ProtectedPage title="Tournament Details" permission="tournaments.view"><TournamentDetailsPage /></ProtectedPage>} />
            <Route path="/tournaments/:id/edit" element={<ProtectedPage title="Edit Tournament" permission="tournaments.update"><EditTournamentPage /></ProtectedPage>} />
            <Route path="/tournaments/:id/bracket" element={<ProtectedPage title="Tournament Bracket" permission="tournaments.view"><TournamentBracketPage /></ProtectedPage>} />

            {/* Teams */}
            <Route path="/teams" element={<ProtectedPage title="Teams" permission="teams.view"><TeamsPage /></ProtectedPage>} />
            <Route path="/teams/create" element={<ProtectedPage title="Create Team" permission="teams.create"><CreateTeamPage /></ProtectedPage>} />
            <Route path="/teams/:id" element={<ProtectedPage title="Team Details" permission="teams.view"><TeamDetailsPage /></ProtectedPage>} />
            <Route path="/teams/:id/edit" element={<ProtectedPage title="Edit Team" permission="teams.update"><EditTeamPage /></ProtectedPage>} />

            {/* Players */}
            <Route path="/players" element={<ProtectedPage title="Players" permission="players.view"><PlayersPage /></ProtectedPage>} />
            <Route path="/players/register" element={<ProtectedPage title="Register Player" permission="players.create"><RegisterPlayerPage /></ProtectedPage>} />
            <Route path="/players/:id/journey" element={<ProtectedPage title="Player Journey" permission="players.view"><PlayerJourneyPage /></ProtectedPage>} />
            <Route path="/players/:id/edit" element={<ProtectedPage title="Edit Player" permission="players.update"><EditPlayerPage /></ProtectedPage>} />

            {/* Analytics & Reports */}
            <Route path="/analytics" element={<ProtectedPage title="Analytics" permission="reports.view"><AnalyticsPage /></ProtectedPage>} />
            <Route path="/app-health" element={<ProtectedPage title="App Health" permission="reports.view"><AppHealthPage /></ProtectedPage>} />
            <Route path="/reports" element={<ProtectedPage title="Reports" permission="reports.view"><ReportsPage /></ProtectedPage>} />

            {/* User Management — admin / super_admin only by virtue of route gate */}
            <Route path="/users" element={<ProtectedPage title="User Management" permission="users.view"><UserManagementPage /></ProtectedPage>} />
            <Route path="/roles" element={<ProtectedPage title="Roles & Permissions" permission="users.view"><RolesPermissionsPage /></ProtectedPage>} />

            {/* Communication */}
            <Route path="/announcements" element={<ProtectedPage title="Announcements" permission="notifications.send"><AnnouncementsPage /></ProtectedPage>} />
            <Route path="/notifications" element={<ProtectedPage title="Notifications"><NotificationsPage /></ProtectedPage>} />

            {/* Profile — always accessible to the logged-in admin */}
            <Route path="/profile" element={<ProtectedPage title="My Profile"><AdminProfilePage /></ProtectedPage>} />

            {/* Support & Settings */}
            <Route path="/support" element={<ProtectedPage title="Support" permission="support.view"><SupportPage /></ProtectedPage>} />
            <Route path="/settings" element={<ProtectedPage title="System Settings" permission="settings.view"><SystemSettingsPage /></ProtectedPage>} />

            {/* Fallback — 404. Wrapped in <ProtectedPage> so the
                signed-in chrome (sidebar + header) still renders around
                the not-found message; an anonymous visitor is bounced
                to login by ProtectedRoute first, same security model as
                before. Replaces the previous silent
                `<Navigate to="/dashboard" />` so a mistyped URL or
                stale link no longer teleports the user with zero
                feedback. */}
            <Route path="*" element={<ProtectedPage title="Page not found"><NotFoundPage /></ProtectedPage>} />
          </Routes>
          </Suspense>
          </BrowserRouter>
          <Toaster />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
